import styled from '@emotion/styled';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';
import CheckboxIcon from 'assets/icons/checkbox.svg?react';
import FiltersIcon from 'assets/icons/filters.svg?react';
import SortIcon from 'assets/icons/iconoir_sort.svg?react';
import Footer from 'components/Footer';
import Header from 'components/Header';
import BgLeft from 'assets/icons/download5.svg?react';
import BgRight from 'assets/icons/download6.svg?react';
import TrollGreetings from 'assets/images/careerTest/troll-greetings.png';
import Helmet from 'components/Helmet';
import LinearProgress from 'components/LinearProgress';
import PageContent from 'components/PageContent';
import PageTitle from 'components/PageTitle';
import coursesEndPoint from 'endPoints/courses';
import MainLayout from 'layouts/MainLayout';
import useDataListFactory from 'services/dataList';
import parseQS from 'helpers/parseQS';
import getLocale, { isLocalizedDomain } from 'helpers/getLocale';
import getLanguageName from 'helpers/getLanguageName';
import formatDirectionName from 'helpers/formatDirectionName';
import { getEducationsDirections, getFilterOptions } from 'actions/educations';
import CoursesCard from './components/CourseCard';
import SearchToggleInput from 'components/SearchToggleInput';
import useInfiniteScroll from 'hooks/useInfiniteScroll';
import { ResultsWrapper, ResultsControls } from 'components/ResultsLayout';
import CardGridSkeleton from 'components/CardGridSkeleton';
const Breadcrumbs = React.lazy(() => import('components/BreadCrumbs'));

const useDataList = (props, options) => useDataListFactory(props, options);

const Title = styled(PageTitle)({});

const Content = styled(PageContent)(({ theme }) => ({
  paddingTop: 30,
  position: 'relative',
  overflow: 'hidden',
  zIndex: 1,
  [theme.breakpoints.down('lg')]: {
    paddingLeft: 20,
    paddingRight: 20
  },
  [theme.breakpoints.down('sm')]: {
    paddingBottom: 40
  }
}));

const Background = styled('section')(({ theme }) => ({
  backgroundColor: theme.palette.tertiary.pale,
  position: 'relative'
}));

const BgLeftStyled = styled(BgLeft)(({ theme }) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const TrollGreetingsStyled = styled('img')(({ theme }) => ({
  position: 'absolute',
  bottom: -100,
  right: 0,
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const TrollGreetingsStyledTop = styled('img')(({ theme }) => ({
  position: 'absolute',
  top: 70,
  right: -120,
  transform: 'rotate(-45deg)',
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const BgRightStyled = styled(BgRight)(({ theme }) => ({
  position: 'absolute',
  right: 0,
  bottom: 0,
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const Wrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: 24,
  alignItems: 'flex-start',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column-reverse'
  }
}));

const LeftSidebar = styled('div')(({ theme }) => ({
  marginBottom: 60,
  flex: 2,
  position: 'relative',
  minHeight: 400,
  [theme.breakpoints.down('md')]: {
    paddingLeft: 0
  }
}));

const RightSidebar = styled('div')(() => ({
  flex: 1,
  position: 'relative',
  alignSelf: 'flex-start'
}));

const BreadcrumbsWrapper = styled('div')(() => ({
  marginBottom: 24
}));

const Filters = styled('div')(({ theme }) => ({
  borderRadius: 18,
  backgroundColor: 'rgba(121, 201, 18, 0.1)',
  padding: 24,
  paddingTop: 16,
  paddingBottom: 16
}));

const FiltersTitle = styled(Typography)(({ theme }) => ({
  fontSize: 20,
  fontWeight: 500,
  marginBottom: 12,
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: 16
  },
  [theme.breakpoints.down('sm')]: {
    cursor: 'pointer',
    marginBottom: 0
  }
}));

const IconButtonStyled = styled(IconButton)(() => ({
  padding: 0,
  margin: 3
}));

const TagsButton = styled(Button)(() => ({
  cursor: 'pointer',
  border: '1px solid rgba(10, 10, 10, 1)',
  padding: '7px 17px',
  borderRadius: 8
}));

const DirectionsContainer = styled('div')(({ theme, showAll, maxRows, isMobile }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  marginBottom: showAll ? 16 : 0,
  overflow: 'hidden',
  maxHeight: showAll ? 'none' : isMobile ? '432px' : '180px', // ~36px per row (button height + gap)
  position: 'relative'
}));

const Card = styled('div')(() => ({
  backgroundColor: '#fff',
  borderRadius: 18,
  border: '1px solid rgba(10, 10, 10, 1)',
  boxShadow: '4px 4px 0px 0px rgba(10, 10, 10, 1)',
  padding: '36px 24px 16px',
  position: 'relative',
  overflow: 'hidden',
  zIndex: 1
}));

const Actions = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 16
}));

const List = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(2, 1fr)`,
  gap: 24,
  minHeight: 600,
  marginBottom: 8,
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: `repeat(1, 1fr)`,
    gap: 16
  }
}));

const FiltersHeadline = styled(Typography)(() => ({
  fontSize: 16,
  fontWeight: 500,
  marginBottom: 0,
  marginTop: 16,
  marginLeft: -2
}));

const CheckboxStyled = styled(Checkbox)(({ theme }) => ({
  color: theme.palette.primary.main,
  padding: '4px 8px',
  '& svg': {
    width: 20,
    height: 20
  }
}));

const FormGroupStyled = styled(FormGroup)(() => ({ 
  marginBottom: 0,
  marginTop: 0,
  '& + &': {
    marginTop: -4
  }
}));

const FormControlLabelStyled = styled(FormControlLabel)(() => ({
  fontSize: 14,
  marginBottom: 0,
  marginTop: 0,
  '& .MuiFormControlLabel-label': {
    fontSize: 14,
    paddingTop: 2
  }
}));

const TextFieldStyledBordered = styled(TextField)(({ theme }) => ({
  border: `1px solid ${theme.palette.primary.main}`,
  backgroundColor: '#fff',
  '& .MuiOutlinedInput-root': {
    height: 36,
    fontSize: 14,
    '& fieldset': {
      borderColor: theme.palette.primary.main
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main
    }
  },
  '& .MuiOutlinedInput-input': {
    padding: '8px 12px',
    fontSize: 14
  }
}));

const SalaryWrapper = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: 20,
  marginBottom: 20,
  '& > div': { marginBottom: 0 }
}));

const FilterActionButton = styled(Button)(({ theme, noMargin, sx = {} }) => ({
  display: 'block',
  borderRadius: 8,
  margin: noMargin ? 0 : '0 auto',
  fontSize: 16,
  fontFamily: 'Unbounded',
  minWidth: 300,
  ...sx,
  [theme.breakpoints.down('sm')]: {
    minWidth: 'unset',
    fontSize: 14,
    width: '100%'
  }
}));

const ButtonStyledWhite = styled(Button)(({ width, theme }) => ({
  borderRadius: 8,
  fontSize: 12,
  fontFamily: 'Unbounded',
  padding: '10px 31px',
  width,
  backgroundColor: theme.palette.white.main,
  color: theme.palette.grey[600],
  zIndex: 10
}));

const ClearFilterActionButton = styled(ButtonStyledWhite)(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[600]}`,
  marginTop: 12,
  width: '100%'
}));

const ShowAllButton = styled(Button)(({ theme }) => ({
  marginTop: 24,
  marginBottom: 16,
  fontSize: 16,
  fontFamily: 'Unbounded',
  textTransform: 'none',
  color: theme.palette.primary.main,
  textDecoration: 'underline',
  cursor: 'pointer',
  display: 'block',
  width: '100%',
  '&:hover': {
    textDecoration: 'underline',
    backgroundColor: 'transparent'
  }
}));

const FilterWrapper = styled('div')(({ theme }) => ({
  [theme.breakpoints.down('sm')]: { marginTop: 24 }
}));

const ResultsLabel = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  paddingLeft: 3,
  color: theme.palette.textDarkGray.main
}));

const PageDescriptionText = styled(ResultsLabel)(() => ({
  marginBottom: 20
}));

const PageDescription = styled(ResultsLabel)(() => ({
  marginTop: 48
}));

const ResultsLabelContainer = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8
}));

const CheckBoxItem = React.memo(({ item, checked, onChange }) => (
  <FormGroupStyled>
    <FormControlLabelStyled
      control={<CheckboxStyled checked={checked} onChange={onChange} icon={<CheckboxIcon />} />}
      label={item}
    />
  </FormGroupStyled>
));

const CoursesList = () => {
  const helmet = useTranslate('HELMET');
  const t = useTranslate('Courses');
  const tDirections = useTranslate('Directions');
  const tPlatforms = useTranslate('Platforms');
  const isMobile = useSelector((state) => state.app.isMobile);
  const [filtersView, setFiltersView] = React.useState(true);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [directions, setDirections] = React.useState([]);
  const [filterOptions, setFilterOptions] = React.useState([]);
  const [showAllCourses, setShowAllCourses] = React.useState(true);
  const [showAllDirections, setShowAllDirections] = React.useState(false);

  // Read initial filters from URL
  const initialFilters = React.useMemo(() => {
    const urlParams = parseQS(location.search);
    const initialFiltersObj = {};
    
    // Helper to convert array to comma-separated string
    const toCommaSeparated = (value) => {
      if (Array.isArray(value)) {
        return value.filter(Boolean).join(',');
      }
      return value;
    };
    
    if (urlParams.filters) {
      if (urlParams.filters.proficiency) {
        initialFiltersObj.proficiency = toCommaSeparated(urlParams.filters.proficiency);
      }
      if (urlParams.filters.language) {
        initialFiltersObj.language = toCommaSeparated(urlParams.filters.language);
      }
      if (urlParams.filters.directions) {
        initialFiltersObj.directions = toCommaSeparated(urlParams.filters.directions);
      }
      if (urlParams.filters.platforms) {
        initialFiltersObj.platforms = toCommaSeparated(urlParams.filters.platforms);
      }
      if (urlParams.filters.duration) {
        initialFiltersObj.duration = toCommaSeparated(urlParams.filters.duration);
      }
      if (urlParams.filters.minCost) {
        initialFiltersObj.minCost = urlParams.filters.minCost;
      }
      if (urlParams.filters.maxCost) {
        initialFiltersObj.maxCost = urlParams.filters.maxCost;
      }
      if (urlParams.filters.searchByCourseName) {
        initialFiltersObj.searchByCourseName = urlParams.filters.searchByCourseName;
      }
    }
    
    return initialFiltersObj;
  }, [location.search]);

  const { actions, data, loading, page, perPage, count, sort, filters } =
    useDataList(coursesEndPoint, { filters: initialFilters });
  const [sortAnchorEl, setSortAnchorEl] = React.useState(null);
  const sortMenuOpen = Boolean(sortAnchorEl);

  const [query, setQuery] = React.useState('');

  // Update URL when filters change
  const updateURLWithFilters = React.useCallback((newFilters) => {
    const urlParams = new URLSearchParams(location.search);
    
    // Remove existing filter params
    Array.from(urlParams.keys()).forEach(key => {
      if (key.startsWith('filters[')) {
        urlParams.delete(key);
      }
    });
    
    // Add new filter params - always use comma-separated strings
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          // Convert array to comma-separated string
          const joined = value.filter(Boolean).join(',');
          if (joined) {
            urlParams.set(`filters[${key}]`, joined);
          }
        } else {
          urlParams.set(`filters[${key}]`, String(value));
        }
      }
    });
    
    const newSearch = urlParams.toString();
    navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const handleOpenChange = React.useCallback(
    (isOpen) => {
      if (!isOpen) {
        setQuery('');
        actions.onSearchChange('');
        const nextFilters = { ...filters };
        delete nextFilters.searchByCourseName;
        updateURLWithFilters(nextFilters);
      }
    },
    [actions, filters, updateURLWithFilters]
  );

  const handleChange = React.useCallback((e) => setQuery(e.target.value), []);

  const handleSearch = React.useCallback(() => {
    actions.onSearchChange(query);
    const nextFilters = { ...filters, searchByCourseName: query };
    updateURLWithFilters(nextFilters);
  }, [actions, query, filters, updateURLWithFilters]);

  const handleToggleFiltersView = React.useCallback(() => {
    if (!isMobile) return;
    setFiltersView((prev) => !prev);
  }, [isMobile]);

  const handleChangePage = React.useCallback(async () => {
    actions.onChangePage(page, true, true);
  }, [actions, page]);

  const handleChangeString = React.useCallback(
    (e, name) => {
      actions.onFilterChange(
        {
          ...filters,
          [name]: e.target.value
        },
        false
      );
    },
    [actions, filters]
  );

  const handleChangeFilter = React.useCallback(
    (name, value) => {
      let nextFilters;
      
      if (typeof value === 'boolean') {
        nextFilters = {
          ...filters,
          [name]: value
        };
      } else {
        const source = (filters[name] || '').split(',') || [];
        const result = source.includes(value)
          ? source.filter((degree) => degree !== value)
          : [...source, value];

        nextFilters = {
          ...filters,
          [name]: result.filter(Boolean).join(',')
        };
      }

      actions.onFilterChange(nextFilters, name === 'directions');
      updateURLWithFilters(nextFilters);
    },
    [actions, filters, updateURLWithFilters]
  );

  const handleSort = React.useCallback(
    (column, direction) => {
      if (sort[column] && sort[column] === direction) {
        actions.onSortChange(column, null);
        setSortAnchorEl(null);
        return;
      }
      actions.onSortChange(column, direction);
      setSortAnchorEl(null);
    },
    [actions, sort]
  );

  const handleClearFilters = React.useCallback(() => {
    actions.onFilterChange({}, true);
    updateURLWithFilters({});
  }, [actions, updateURLWithFilters]);

  const isDurationChecked = React.useCallback(
    (value) => {
      const [min, max] = value;
      const key = `${min}, ${Number.isFinite(max) ? max : ''}`;
      const current = filters.duration;
      if (Array.isArray(current)) {
        return current.includes(key);
      }
      if (typeof current === 'string') {
        return current === key;
      }
      return false;
    },
    [filters]
  );

  const handleChangeDuration = React.useCallback(
    (value) => {
      const [min, max] = value;
      const key = `${min}, ${Number.isFinite(max) ? max : ''}`;
      const current = filters.duration;

      const next = current === key ? undefined : key;
      const nextFilters = {
        ...filters,
        duration: next
      };

      actions.onFilterChange(nextFilters, false);
      updateURLWithFilters(nextFilters);
    },
    [actions, filters, updateURLWithFilters]
  );

  React.useEffect(() => {
    dispatch(getEducationsDirections()).then((res) => {
      if (Array.isArray(res)) setDirections(res);
      else setDirections([]);
    });
  }, [dispatch]);

  const filtersRef = React.useRef(filters);
  React.useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Fetch filter options with current filters applied
  React.useEffect(() => {
    // Prepare filters for the API call
    const normalizedFilters = {};
    Object.entries(filtersRef.current || {}).forEach(([key, value]) => {
      if (value && typeof value === 'string' && value.includes(',')) {
        normalizedFilters[key] = value.split(',').filter(Boolean);
      } else if (value !== undefined && value !== null && value !== '') {
        normalizedFilters[key] = value;
      }
    });

    dispatch(getFilterOptions(normalizedFilters)).then((res) => {
      if (res && typeof res === 'object' && res.data) {
        setFilterOptions(res.data);
      } else if (res && typeof res === 'object') {
        setFilterOptions(res);
      } else {
        setFilterOptions({});
      }
    });
  }, [dispatch, filters.proficiency, filters.language, filters.directions, filters.platforms]);

  // Set default language filter based on platform
  React.useEffect(() => {
    // Only set default if no language filter is already applied
    if (!filters.language) {
      // For profitroll: default to Ukrainian (uk)
      // For jobalto: default to current website locale
      const defaultLanguage = isLocalizedDomain() ? getLocale() : 'uk';
      actions.onFilterChange({
        ...filters,
        language: defaultLanguage
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset showAllCourses when filters change (but not when data changes due to pagination)
  React.useEffect(() => {
    setShowAllCourses(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);


  const limitReached = React.useMemo(() => count <= page * perPage, [count, page, perPage]);

  const sortValues = React.useMemo(
    () => [
      { column: 'courseName', direction: 'asc', name: t('ByNameAsc') },
      { column: 'courseName', direction: 'desc', name: t('ByNameDesc') },
      { column: 'price', direction: 'asc', name: t('ByPriceAsc') },
      { column: 'price', direction: 'desc', name: t('ByPriceDesc') },
      { column: 'duration', direction: 'asc', name: t('ByDurationAsc') },
      { column: 'duration', direction: 'desc', name: t('ByDurationDesc') }
    ],
    [t]
  );

  const directionTags = React.useMemo(
    () =>
      directions.map((topic) => {
        // Tolerate both the legacy string shape and the new {slug,name} object
        // (depending on which backend version is deployed).
        const value = topic?.slug ?? topic;
        const selected = (filters.directions || '').split(',').includes(value);
        return (
          <TagsButton
            key={value}
            variant={selected ? 'contained' : 'outlined'}
            color={selected ? 'primary' : 'inherit'}
            onClick={() => handleChangeFilter('directions', value)}>
            {formatDirectionName(topic?.name ?? topic, tDirections)}
          </TagsButton>
        );
      }),
    [directions, filters.directions, handleChangeFilter, tDirections]
  );

  // Calculate if we need to show "Show more" button
  // Approximate: each button is ~36px tall (including gap)
  // Desktop: ~5-6 tags per row, 5 rows = ~25-30 tags
  // Mobile: ~2-3 tags per row, 12 rows = ~24-36 tags
  const maxRows = isMobile ? 12 : 5;
  const estimatedTagsPerRow = isMobile ? 3 : 6;
  const maxVisibleTags = maxRows * estimatedTagsPerRow;
  const hasMoreDirections = directions.length > maxVisibleTags && !showAllDirections;
  
  // Reset showAllDirections when directions change
  React.useEffect(() => {
    setShowAllDirections(false);
  }, [directions]);

  const INITIAL_COURSES_LIMIT = 6;
  
  const courseCards = React.useMemo(
    () => (data || []).map((item, idx) => <CoursesCard key={idx} item={item} />),
    [data]
  );

  const displayedCourseCards = React.useMemo(() => {
    if (showAllCourses || !data || data.length <= INITIAL_COURSES_LIMIT) {
      return courseCards;
    }
    return courseCards.slice(0, INITIAL_COURSES_LIMIT);
  }, [courseCards, showAllCourses, data]);

  // Show "Show all" button if there are more than INITIAL_COURSES_LIMIT courses in current view
  const hasMoreCourses = React.useMemo(() => {
    if (!data || showAllCourses) return false;
    // Show button if we have more than INITIAL_COURSES_LIMIT courses in the current data
    return data.length > INITIAL_COURSES_LIMIT;
  }, [data, showAllCourses]);

  const { sentinelRef } = useInfiniteScroll({
    isLoading: loading,
    hasMore: !limitReached,
    onLoadMore: handleChangePage,
    rootMargin: '200px 0px',
    threshold: 0
  });

  const breadcrumbs = React.useMemo(() => [
    { link: '/', label: 'Home' },
    { link: '/education', label: 'Education' },
    { link: '/education/platforms', label: tPlatforms('Title'), raw: true },
    { link: '/education/platforms/courses', label: t('Title'), raw: true }
  ], [t, tPlatforms]);

  return (
    <>
      <Helmet
        meta={{
          title: helmet('coursesTitle'),
          description: helmet('coursesDescription'),
          ogTitle: helmet('coursesOgTitle'),
          ogDescription: helmet('coursesOgDescription')
        }}
      />
      <MainLayout>
        <Header />
        <Background>
          <Content style={{ paddingBottom: 20 }}>
          <React.Suspense fallback={null}>
            <BreadcrumbsWrapper>
              <Breadcrumbs breadcrumbs={breadcrumbs} />
            </BreadcrumbsWrapper>
          </React.Suspense>
          <Card>
            <Title variant="h1">{t('Title')}</Title>

            <Actions>
              <ResultsLabel>{t('SearchPrompt')}</ResultsLabel>
              {!isMobile ? (
                <SearchToggleInput
                  query={query}
                  onChange={handleChange}
                  onSearch={handleSearch}
                  onOpenChange={handleOpenChange}
                  placeholder={t('SearchPlaceholder')}
                  openAriaLabel={t('OpenSearch')}
                  closeAriaLabel={t('CloseSearch')}
                  searchAriaLabel={t('Search')}
                />
              ) : null}
            </Actions>

            <DirectionsContainer showAll={showAllDirections} maxRows={maxRows} isMobile={isMobile}>
              {directionTags}
            </DirectionsContainer>
            {hasMoreDirections && (
              <ShowAllButton
                onClick={() => setShowAllDirections(true)}
                variant="text"
                fullWidth>
                {t('ShowAll')}
              </ShowAllButton>
            )}
          </Card>
        </Content>

        <Content style={{ paddingTop: 0 }}>
          <Wrapper>
            <LeftSidebar>
              <ResultsWrapper>
                {data && data.length === 0 ? (
                  <ResultsLabel>{t('NoData')}</ResultsLabel>
                ) : (
                  <>
                    <ResultsLabelContainer>
                      <ResultsLabel>{t('FoundResults', { count })}</ResultsLabel>
                      {loading && <CircularProgress size={16} />}
                    </ResultsLabelContainer>
                    <ResultsControls>
                      {isMobile ? (
                        <SearchToggleInput
                          query={query}
                          onChange={handleChange}
                          onSearch={handleSearch}
                          onOpenChange={handleOpenChange}
                          placeholder={t('SearchPlaceholder')}
                          openAriaLabel={t('OpenSearch')}
                          closeAriaLabel={t('CloseSearch')}
                          searchAriaLabel={t('Search')}
                        />
                      ) : null}
                      <IconButtonStyled
                        aria-label={t('Sort')}
                        onClick={(e) => setSortAnchorEl(e.currentTarget)}>
                        <SortIcon />
                      </IconButtonStyled>
                    </ResultsControls>
                    <Menu
                      anchorEl={sortAnchorEl}
                      open={sortMenuOpen}
                      onClose={() => setSortAnchorEl(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                      {sortValues.map((item) => (
                        <MenuItem
                          key={`${item.column}-${item.direction}`}
                          selected={sort[item.column] === item.direction}
                          onClick={() => handleSort(item.column, item.direction)}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}
              </ResultsWrapper>

              <List>{displayedCourseCards}</List>

              {hasMoreCourses && (
                <ShowAllButton
                  onClick={() => setShowAllCourses(true)}
                  variant="text"
                  fullWidth>
                  {t('ShowAll')}
                </ShowAllButton>
              )}

              {!data && loading ? <CardGridSkeleton count={6} /> : null}

            {loading || !data ? <LinearProgress /> : null}

              <div ref={sentinelRef} aria-label={t('LoadMore')} style={{ height: 1 }} />

              <PageDescription component={'div'}>
                <PageDescriptionText>{t('Description1')}</PageDescriptionText>
                <PageDescriptionText>{t('Description2')}</PageDescriptionText>
                <PageDescriptionText>{t('Description3')}</PageDescriptionText>
              </PageDescription>
            </LeftSidebar>

            <RightSidebar>
              <Filters>
                <FiltersTitle onClick={handleToggleFiltersView}>
                  <FiltersIcon />
                  {t('filtersTitle')}
                </FiltersTitle>

                {filtersView ? (
                  <FilterWrapper>
                    <FiltersHeadline>{t('Proficiency')}</FiltersHeadline>
                    {(filterOptions?.proficiency || []).map((level) => (
                      <CheckBoxItem
                        key={level}
                        item={level}
                        checked={(filters.proficiency || '').split(',').includes(level)}
                        onChange={() => handleChangeFilter('proficiency', level)}
                      />
                    ))}

                    <FiltersHeadline>{t('Language')}</FiltersHeadline>
                    {(filterOptions?.languages || []).map((lang) => (
                      <CheckBoxItem
                        key={lang}
                        item={getLanguageName(lang)}
                        checked={(filters.language || '').split(',').includes(lang)}
                        onChange={() => handleChangeFilter('language', lang)}
                      />
                    ))}

                    <FiltersHeadline>{t('Duration')}</FiltersHeadline>
                    {[
                      {
                        value: [0, 1],
                        label: t('1day')
                      },
                      {
                        value: [1, 30],
                        label: t('1month')
                      },
                      {
                        value: [30, 90],
                        label: t('1-3months')
                      },
                      {
                        value: [90, 180],
                        label: t('3-6months')
                      },
                      {
                        value: [180, 365],
                        label: t('6-12months')
                      },
                      {
                        value: [365, 365 * 5],
                        label: t('from1year')
                      }
                    ].map((item) => (
                      <CheckBoxItem
                        key={item.label}
                        item={item.label}
                        checked={isDurationChecked(item.value)}
                        onChange={() => handleChangeDuration(item.value)}
                      />
                    ))}

                    <FiltersHeadline>{t('Cost')}</FiltersHeadline>
                    <SalaryWrapper>
                      <TextFieldStyledBordered
                        value={filters?.cost_min || ''}
                        onChange={(e) => handleChangeString(e, 'cost_min')}
                        placeholder={t('from')}
                        variant="outlined"
                        fullWidth
                        slotProps={{
                          htmlInput: { inputMode: 'numeric' }
                        }}
                      />

                      <TextFieldStyledBordered
                        value={filters?.cost_max || ''}
                        onChange={(e) => handleChangeString(e, 'cost_max')}
                        placeholder={t('to')}
                        variant="outlined"
                        fullWidth
                        slotProps={{
                          htmlInput: { inputMode: 'numeric' }
                        }}
                      />
                    </SalaryWrapper>

                    <FilterActionButton
                      variant="contained"
                      color="primary"
                      onClick={() => actions.load()}
                      fullWidth={true}
                      aria-label={t('handleSearch')}
                      sx={{ marginTop: 40 }}>
                      {t('handleSearch')}
                    </FilterActionButton>

                    <ClearFilterActionButton
                      variant="outlined"
                      fullWidth={true}
                      onClick={handleClearFilters}>
                      {t('ClearAllFilters')}
                    </ClearFilterActionButton>
                  </FilterWrapper>
                ) : null}
              </Filters>
            </RightSidebar>
          </Wrapper>
          <TrollGreetingsStyled src={TrollGreetings} alt="" />
        </Content>
        <TrollGreetingsStyledTop src={TrollGreetings} alt="" />
        <BgLeftStyled />
        <BgRightStyled />
        </Background>
        <Footer />
      </MainLayout>
    </>
  );
};

export default CoursesList;
